import React, { useState } from 'react';
import { TextField, FunctionField } from 'react-admin';
import { IconButton, makeStyles, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import { capitalize } from 'lodash';

import { List, Datagrid } from 'components/list';
import { ActionField, DateField, PriceField } from 'components/fields';
import ActionButton from 'components/buttons/ActionButton';
import CreateCampaignDialog from './components/CreateCampaignDialog';
import DeleteCampaignActionField from './components/DeleteCampaingActionField';
import ExportCampaignActionField from './components/ExportCampaignActionField';
import DeferredCreditDetailsDialog, {
  DeferredCreditCampaignRecord,
} from './components/DeferredCreditDetailsDialog';
import formatPrice from 'services/helpers/formatPrice';
import { AdminWriteAccess } from 'contexts/adminAccess';

const useStyles = makeStyles((theme) => ({
  deferred: {
    display: 'inline-flex',
    alignItems: 'center',
  },

  infoButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.secondary.contrastText,
    flexShrink: 0,
  },
}));

const DeferredCreditField = ({ record }: { record: DeferredCreditCampaignRecord }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { deferred, currency = 'USD' } = record.details || {};

  if (!deferred) {
    return <>—</>;
  }

  return (
    <span className={classes.deferred} onClick={(event) => event.stopPropagation()}>
      <span>
        {`After ${formatPrice(deferred.spendThreshold, { currency })} ${currency} spent within ${deferred.earnWindowDays} days after signup`}
      </span>
      <Tooltip title="View deferred credit details">
        <IconButton
          className={classes.infoButton}
          size="small"
          aria-label="View deferred credit details"
          onClick={() => setOpen(true)}>
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      {open && (
        <DeferredCreditDetailsDialog open={open} record={record} onClose={() => setOpen(false)} />
      )}
    </span>
  );
};

const CreditCodesActions = () => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <ActionButton onClick={handleOpen} startIcon={<AddIcon />}>
        Create campaign
      </ActionButton>

      {open && <CreateCampaignDialog open={open} onCancel={handleClose} />}
    </>
  );
};

const CreditCodesList = (props) => {
  return (
    <List
      {...props}
      actions={
        <AdminWriteAccess>
          <CreditCodesActions />
        </AdminWriteAccess>
      }
      sort={{ field: 'createdAt', order: 'DESC' }}>
      <Datagrid>
        <TextField source="id" label="Campaing ID" />

        <TextField source="name" label="Campaing name" />

        <DateField label="Start" source="startAt" withTime />

        <DateField label="End" source="endAt" withTime />

        <TextField source="numberOfCodes" label="Number of codes" />

        <FunctionField
          source="details.redeemLimitOfCodes"
          label="Usage limit per code"
          render={(record) => {
            const limit = record.details.redeemLimitOfCodes;

            return limit === 0 || (typeof limit === 'string' && /^0+$/.test(limit))
              ? 'Unlimited'
              : limit ?? '';
          }}
        />

        <FunctionField label="Type" render={(record) => capitalize(record.details.type)} />

        <FunctionField
          label="Amount"
          render={(record) =>
            record.details.type === 'fund' ? (
              <PriceField
                record={record}
                source="details.priceOfCodes"
                currencySource="details.currency"
              />
            ) : (
              `${record.details.priceOfCodes} ${record.details.priceOfCodes > 1 ? 'labels' : 'label'}`
            )
          }
        />

        <FunctionField
          label="Deferred"
          render={(record) => <DeferredCreditField key={record.id} record={record} />}
        />

        <TextField source="numberOfRedeemed" label="Redeemed" />

        <ActionField>
          <ExportCampaignActionField />

          <DeleteCampaignActionField />
        </ActionField>
      </Datagrid>
    </List>
  );
};

export default CreditCodesList;
